<template>
  <div
    class="relative bg-gradient-to-br from-gray-50 via-white to-logo-color/5 overflow-hidden"
  >
    <!-- Background Decorative Shapes -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-40 left-10 w-80 h-80 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-40 right-10 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <!-- Geometric shapes -->
      <div
        class="absolute top-20 right-40 w-24 h-24 border-4 border-logo-color/10 rounded-full hidden lg:block"
      ></div>
      <div
        class="absolute bottom-60 left-40 w-20 h-20 bg-logo-color/10 rotate-45 hidden lg:block"
      ></div>
      <svg
        class="absolute top-1/3 right-1/4 w-40 h-40 text-logo-color/5 hidden lg:block"
        viewBox="0 0 100 100"
      >
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="currentColor"
          stroke-width="8"
        />
      </svg>
      <svg
        class="absolute bottom-1/4 left-1/3 w-32 h-32 text-logo-color/5 hidden lg:block"
        viewBox="0 0 100 100"
      >
        <polygon points="50,5 95,95 5,95" fill="currentColor" />
      </svg>
    </div>

    <!-- Main Content -->
    <div class="relative pt-20 pb-32 z-10">
      <!-- Header Section -->
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div class="max-w-4xl mx-auto text-center" data-aos="fade-up">
          <!-- Badge -->
          <span
            class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full mb-6"
          >
            <span
              class="w-2 h-2 bg-logo-color rounded-full animate-pulse"
            ></span>
            <span
              class="text-sm font-semibold text-logo-color uppercase tracking-wider"
              >Our Programs</span
            >
          </span>

          <!-- Main title -->
          <h1
            class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Explore Our
            <span class="text-logo-color relative">
              Programs
              <svg
                class="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M1 5.5C47.6667 2.16667 141.4 -2.1 199 5.5"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  class="text-logo-color/30"
                />
              </svg>
            </span>
          </h1>
          <p class="max-w-2xl mx-auto text-lg text-gray-600">
            Practical hands-on training, mentorship and coaching empowering
            youth to be self-reliant agents of change
          </p>
        </div>
      </div>

      <!-- Services Grid with Images -->
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            v-for="(service, index) in services"
            :key="index"
            class="group"
            :data-aos="'fade-up'"
            :data-aos-delay="index * 100"
          >
            <!-- Card -->
            <div
              class="relative h-full bg-white rounded-3xl overflow-hidden transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.2)] hover:-translate-y-2"
            >
              <!-- Image Section -->
              <div class="relative h-48 overflow-hidden">
                <img
                  :src="service.image"
                  :alt="service.title"
                  class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
                ></div>

                <!-- Service number badge -->
                <div
                  class="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center text-logo-color font-bold text-sm shadow-lg"
                >
                  {{ (index + 1).toString().padStart(2, "0") }}
                </div>

                <!-- Icon overlay -->
                <div class="absolute bottom-4 left-4">
                  <div
                    class="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                  >
                    <i :class="['fas', service.icon, 'text-xl text-white']"></i>
                  </div>
                </div>
              </div>

              <!-- Content Section -->
              <div class="p-6">
                <!-- Title -->
                <h2
                  class="text-xl font-bold text-gray-900 mb-3 group-hover:text-logo-color transition-colors"
                >
                  {{ service.title }}
                </h2>

                <!-- Description -->
                <p class="text-gray-600 text-sm mb-5 leading-relaxed">
                  {{ service.description }}
                </p>

                <!-- Features -->
                <div class="space-y-2">
                  <div
                    v-for="(feature, fIndex) in service.features"
                    :key="fIndex"
                    class="flex items-center gap-3"
                  >
                    <div
                      class="flex-shrink-0 w-6 h-6 rounded-full bg-logo-color/10 flex items-center justify-center"
                    >
                      <svg
                        class="w-3 h-3 text-logo-color"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span class="text-gray-600 text-sm">{{ feature }}</span>
                  </div>
                </div>
              </div>

              <!-- Hover overlay effect -->
              <div
                class="absolute inset-0 border-2 border-transparent group-hover:border-logo-color rounded-3xl transition-all duration-300 pointer-events-none"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div class="relative rounded-3xl overflow-hidden" data-aos="fade-up">
          <!-- Background Image -->
          <div class="absolute inset-0">
            <img
              :src="require('@/assets/3dprinting.jpg')"
              alt="Youth Innovation"
              class="w-full h-full object-cover"
            />
            <div
              class="absolute inset-0 bg-gradient-to-r from-logo-color/95 via-logo-color/90 to-logo-color/80"
            ></div>
          </div>

          <!-- Decorative shapes -->
          <div class="absolute inset-0 pointer-events-none">
            <div
              class="absolute top-10 right-10 w-32 h-32 border-4 border-white/10 rounded-full"
            ></div>
            <div
              class="absolute bottom-10 left-10 w-24 h-24 bg-white/5 rotate-45"
            ></div>
            <svg
              class="absolute top-1/2 right-1/4 w-20 h-20 text-white/10"
              viewBox="0 0 100 100"
            >
              <polygon points="50,5 95,95 5,95" fill="currentColor" />
            </svg>
          </div>

          <!-- Content -->
          <div class="relative px-8 py-16 md:px-16 md:py-20">
            <div class="max-w-3xl mx-auto text-center text-white">
              <div
                class="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
              >
                <span
                  class="w-2 h-2 bg-white rounded-full animate-pulse"
                ></span>
                <span class="text-sm font-semibold uppercase tracking-wider"
                  >Get Started</span
                >
              </div>
              <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Ready to Start Your Journey?
              </h2>
              <p class="text-lg mb-8 text-white/90 max-w-xl mx-auto">
                Join us and gain the skills to compete and lead in the global
                economy
              </p>

              <!-- Buttons -->
              <div class="flex flex-wrap justify-center gap-4">
                <button
                  @click="showModal = true"
                  class="group inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white font-semibold text-logo-color hover:shadow-xl hover:shadow-black/20 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <span>Get Started</span>
                  <svg
                    class="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
                <router-link
                  to="/about"
                  class="inline-flex items-center gap-3 px-8 py-4 rounded-xl border-2 border-white/30 font-semibold text-white hover:bg-white/10 transition-all duration-300"
                >
                  <span>Learn More</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Modal -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/60 backdrop-blur-sm"
          @click="showModal = false"
        ></div>

        <!-- Modal Content -->
        <div
          class="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          <!-- Header -->
          <div class="bg-logo-color px-6 py-8 text-white text-center">
            <div
              class="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <i class="fas fa-headset text-3xl"></i>
            </div>
            <h3 class="text-2xl font-bold">Contact Information</h3>
            <p class="text-white/80 mt-2">We'd love to hear from you</p>
          </div>

          <!-- Close Button -->
          <button
            @click="showModal = false"
            class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>

          <!-- Contact Options -->
          <div class="p-6 space-y-4">
            <!-- Email -->
            <a
              href="mailto:support@youthallynow.org"
              class="flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-logo-color hover:bg-logo-color/5 transition-all group"
            >
              <div
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center group-hover:bg-logo-color transition-colors"
              >
                <i
                  class="fas fa-envelope text-logo-color group-hover:text-white transition-colors"
                ></i>
              </div>
              <div class="ml-4 flex-1">
                <div class="text-sm text-gray-500">Email</div>
                <div class="text-gray-800 font-semibold">
                  support@youthallynow.org
                </div>
              </div>
              <i
                class="fas fa-chevron-right text-gray-400 group-hover:text-logo-color transition-colors"
              ></i>
            </a>

            <!-- Phone -->
            <a
              href="tel:+233249372583"
              class="flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-logo-color hover:bg-logo-color/5 transition-all group"
            >
              <div
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center group-hover:bg-logo-color transition-colors"
              >
                <i
                  class="fas fa-phone-alt text-logo-color group-hover:text-white transition-colors"
                ></i>
              </div>
              <div class="ml-4 flex-1">
                <div class="text-sm text-gray-500">Phone</div>
                <div class="text-gray-800 font-semibold">+233 24 937 2583</div>
              </div>
              <i
                class="fas fa-chevron-right text-gray-400 group-hover:text-logo-color transition-colors"
              ></i>
            </a>

            <!-- WhatsApp -->
            <a
              href="https://wa.me/+233249372583"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-green-500 hover:bg-green-50 transition-all group"
            >
              <div
                class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors"
              >
                <i
                  class="fab fa-whatsapp text-xl text-green-600 group-hover:text-white transition-colors"
                ></i>
              </div>
              <div class="ml-4 flex-1">
                <div class="text-sm text-gray-500">WhatsApp</div>
                <div class="text-gray-800 font-semibold">Quick Response</div>
              </div>
              <i
                class="fas fa-chevron-right text-gray-400 group-hover:text-green-500 transition-colors"
              ></i>
            </a>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ServicesTypes",
  data() {
    return {
      showModal: false,
      services: [
        {
          title: "Ethical & Transformative Leadership",
          description:
            "Inculcating critical traits of ethics, integrity, hardwork, passion, honesty and transformational leadership in young people.",
          icon: "fa-hand-holding-heart",
          image: require("@/assets/leadership.jpg"),
          features: [
            "Ethics & Integrity Training",
            "Critical Thinking Skills",
            "Growth Mindset Development",
          ],
        },
        {
          title: "Work Readiness Program",
          description:
            "Developing the capacity, ambition and confidence of young people to master relevant soft skills for workplace success.",
          icon: "fa-briefcase",
          image: require("@/assets/workready.jpg"),
          features: [
            "Soft Skills Training",
            "Interview Preparation",
            "Workplace Ethics",
          ],
        },
        {
          title: "Mentorship & Coaching",
          description:
            "Access to mentors and coaches across different professions who provide support, advice and guidance to young people.",
          icon: "fa-users",
          image: require("@/assets/mentorship.jpg"),
          features: [
            "One-on-One Mentoring",
            "Career Guidance",
            "Professional Networking",
          ],
        },
        {
          title: "Entrepreneurship & Enterprise Development",
          description:
            "Enabling young people to develop home-grown innovative solutions to unique problems in their communities.",
          icon: "fa-lightbulb",
          image: require("@/assets/enterpreneurship.jpeg"),
          features: [
            "Business Development",
            "Innovation Support",
            "Market Connections",
          ],
        },
        {
          title: "School Improvements Program",
          description:
            "Providing support to basic schools in the form of physical infrastructure and retooling for a conducive learning environment.",
          icon: "fa-school",
          image: require("@/assets/schoolimprovement.jpg"),
          features: [
            "Infrastructure Support",
            "Learning Resources",
            "Capacity Building",
          ],
        },
        {
          title: "Different Intelligences Conference",
          description:
            "Annual conference inspiring young talented Ghanaians to showcase exceptional abilities and explore creativity for national development.",
          icon: "fa-star",
          image: require("@/assets/conferences.jpg"),
          features: [
            "Talent Showcase",
            "Creativity Workshops",
            "Problem-Solving Challenges",
          ],
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Modern design with shapes and images */
</style>
